import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { Notification, User } from 'src/v3/core/types'
import {
  maintenanceConfig,
  MaintenanceWindow,
} from 'src/v3/core/maintenance-config'
import { dayDiffText } from 'src/v3/utilities/dates'

type UserSettings = {
  isNewYork: boolean
}

type BoundTreeStatus = 'subscribed' | 'limited' | 'unsubscribed'

type StripePrice = {
  amount: number
  priceID: string
  productName: string
}

type PromoResponse = {
  valid: boolean
  message?: string
  code?: string
  discount?: number
  discountType?: string
  originalAmount?: number
  discountedAmount?: number
  newSubscriptionDetails: {
    subscriptionId: string
    clientSecret?: string
  }
}

export type StatesResponse = {
  States: string[]
  Territories: string[]
  Miscellaneous: string[]
  Countries: string[]
}

export type TimeZoneData = {
  name: string
  displayName: string
  utcOffset: string
}

type SettingsSlice = {
  settings?: UserSettings
  settingsStatus: Status
  sessionStatus: Status
  featureFlagsEvaluted: boolean
  roles: string[]
  emailAddress: string
  linkedServices: string[]
  userID: string
  boundTreeStatus: BoundTreeStatus
  navigationStatus: Record<string, boolean>
  useScribeAccreditationAnalysis: boolean
  useEvents: boolean
  useSubscribePageV2: boolean
  useTranscriptEditor: boolean
  useCreateV4: boolean
  useCreateV5: boolean
  useShowNotes: boolean
  useLiveClassV4: boolean
  useProfileAssignmentsv4: boolean
  useRecertificationCycle: boolean
  useCompliance: boolean
  useAssignmentAnalytics: boolean
  useGroupsV2: boolean
  useIBSCV2: boolean
  notifications: Notification[]
  notificationsStatus: Status
  user?: User
  userStatus: Status
  profileImage: string
  profileImageStatus: Status
  checkoutPrice?: StripePrice
  checkoutPromoCode: string
  checkoutPromoResponse?: PromoResponse
  checkoutClientSecret: string
  checkoutPromoValidationStatus:
    'validating' | 'invalid' | 'valid' | 'error' | 'uninitialized'
  states?: StatesResponse
  statesStatus: Status
  timeZones: TimeZoneData[]
  timeZonesStatus: Status
  scheduledMaintenance: MaintenanceWindow | null
  maintenanceConfigStatus: Status
  maintenanceBannerDismissed: boolean
  useMaintenanceMode: boolean
  useTexasJurisprudence: boolean
  useEventsManagerDashboard: boolean
  useEventsStudentView: boolean
  useQuizAnalyticsV2: boolean
}

export let initialState: SettingsSlice = {
  settings: undefined,
  settingsStatus: 'uninitialized',
  sessionStatus: 'uninitialized',
  featureFlagsEvaluted: false,
  roles: [],
  emailAddress: '',
  linkedServices: [],
  userID: '',
  boundTreeStatus: 'unsubscribed',
  navigationStatus: {
    open: false,
    Learning: true,
    Progress: false,
    Admin: false,
    Manage: false,
    Create: false,
  },
  useScribeAccreditationAnalysis: false,
  useEvents: false,
  useSubscribePageV2: false,
  useTranscriptEditor: false,
  useCreateV4: false,
  useCreateV5: false,
  useShowNotes: false,
  useLiveClassV4: false,
  useProfileAssignmentsv4: false,
  useRecertificationCycle: false,
  useCompliance: false,
  useAssignmentAnalytics: false,
  useGroupsV2: false,
  useIBSCV2: false,
  notifications: [],
  notificationsStatus: 'uninitialized',
  user: undefined,
  userStatus: 'uninitialized',
  profileImage: '',
  profileImageStatus: 'uninitialized',
  checkoutPromoCode: '',
  checkoutPromoValidationStatus: 'uninitialized',
  checkoutClientSecret: '',
  statesStatus: 'uninitialized',
  timeZones: [],
  timeZonesStatus: 'uninitialized',
  scheduledMaintenance: null,
  maintenanceConfigStatus: 'uninitialized',
  maintenanceBannerDismissed: false,
  useMaintenanceMode: false,
  useTexasJurisprudence: false,
  useEventsManagerDashboard: false,
  useEventsStudentView: false,
  useQuizAnalyticsV2: false,
}

export let fetchSettings = createAsyncThunk('settings/fetchSettings', () =>
  axios.get<UserSettings>('/api/settings').then(pluckData),
)

export let fetchNotifications = createAsyncThunk(
  'settings/fetchNotifications',
  () =>
    axios
      .get<{ [kind: string]: Notification }[]>('/api/settings/notifications')
      .then(pluckData)
      .then((data) =>
        data.map((n): Notification => {
          let [kind, obj] = Object.entries(n)[0]
          return { ...obj, kind: kind as any }
        }),
      ),
)

export let fetchUser = createAsyncThunk('settings/fetchUser', () =>
  axios.get<User>('/api/settings/user').then(pluckData),
)

export let fetchProfileImage = createAsyncThunk(
  'settings/fetchProfileImage',
  () => axios.get<string>('/api/settings/profile-image').then(pluckData),
)

export let fetchStates = createAsyncThunk('settings/fetchStates', () =>
  axios.get<StatesResponse>('/api/settings/states').then(pluckData),
)

export let fetchTimeZones = createAsyncThunk('settings/fetchTimeZones', () =>
  axios.get<TimeZoneData[]>('/api/settings/time-zones').then(pluckData),
)

// Phase 1: reads from hardcoded TS file. Phase 2: replace Promise.resolve with an API call.
export let fetchMaintenanceConfig = createAsyncThunk(
  'settings/fetchMaintenanceConfig',
  () => Promise.resolve(maintenanceConfig),
)

export let settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    sessionStatusChanged: (state, action: PayloadAction<Status>) => {
      state.sessionStatus = action.payload
    },
    featureFlagsFlagEvaluated: (state) => {
      state.featureFlagsEvaluted = true
    },
    rolesChnaged: (state, action: PayloadAction<string[]>) => {
      state.roles = action.payload
    },
    emailAddressChanged: (state, action: PayloadAction<string>) => {
      state.emailAddress = action.payload
    },
    linkedServicesChanged: (state, action: PayloadAction<string[]>) => {
      state.linkedServices = action.payload
    },
    userIDChanged: (state, action: PayloadAction<string>) => {
      state.userID = action.payload
    },
    boundTreeStatusChanged: (state, action: PayloadAction<BoundTreeStatus>) => {
      state.boundTreeStatus = action.payload
    },
    navigationStatusChanged: (
      state,
      action: PayloadAction<Record<string, boolean>>,
    ) => {
      state.navigationStatus = action.payload
    },
    useScribeAccreditationAnalysisChanged: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.useScribeAccreditationAnalysis = action.payload
    },
    useEventsChanged: (state, action: PayloadAction<boolean>) => {
      state.useEvents = action.payload
    },
    useSubscribePageV2Changed: (state, action: PayloadAction<boolean>) => {
      state.useSubscribePageV2 = action.payload
    },
    useTranscriptEditorChanged: (state, action: PayloadAction<boolean>) => {
      state.useTranscriptEditor = action.payload
    },
    useCreateV4Changed: (state, action: PayloadAction<boolean>) => {
      state.useCreateV4 = action.payload
    },
    useCreateV5Changed: (state, action: PayloadAction<boolean>) => {
      state.useCreateV5 = action.payload
    },
    useShowNotesChanged: (state, action: PayloadAction<boolean>) => {
      state.useShowNotes = action.payload
    },
    useLiveClassV4Changed: (state, action: PayloadAction<boolean>) => {
      state.useLiveClassV4 = action.payload
    },
    useProfileAssignmentsv4Changed: (state, action: PayloadAction<boolean>) => {
      state.useProfileAssignmentsv4 = action.payload
    },
    useRecertificationCycleChanged: (state, action: PayloadAction<boolean>) => {
      state.useRecertificationCycle = action.payload
    },
    useComplianceChanged: (state, action: PayloadAction<boolean>) => {
      state.useCompliance = action.payload
    },
    useAssignmentAnalyticsChanged: (state, action: PayloadAction<boolean>) => {
      state.useAssignmentAnalytics = action.payload
    },
    useGroupsV2Changed: (state, action: PayloadAction<boolean>) => {
      state.useGroupsV2 = action.payload
    },
    useIBSCV2Changed: (state, action: PayloadAction<boolean>) => {
      state.useIBSCV2 = action.payload
    },
    checkoutPriceChanged: (state, action: PayloadAction<StripePrice>) => {
      state.checkoutPrice = action.payload
    },
    checkoutPromoCodeChanged: (state, action: PayloadAction<string>) => {
      state.checkoutPromoCode = action.payload
    },
    checkoutPromoValidationStatusChanged: (
      state,
      action: PayloadAction<SettingsSlice['checkoutPromoValidationStatus']>,
    ) => {
      state.checkoutPromoValidationStatus = action.payload
    },
    checkoutPromoResponseChanged: (
      state,
      action: PayloadAction<PromoResponse>,
    ) => {
      state.checkoutPromoResponse = action.payload
    },
    checkoutClientSecretChanged: (state, action: PayloadAction<string>) => {
      state.checkoutClientSecret = action.payload
    },
    maintenanceBannerDismissed: (state) => {
      state.maintenanceBannerDismissed = true
    },
    useMaintenanceModeChanged: (state, action: PayloadAction<boolean>) => {
      state.useMaintenanceMode = action.payload
    },
    useTexasJurisprudenceChanged: (state, action: PayloadAction<boolean>) => {
      state.useTexasJurisprudence = action.payload
    },
    useEventsManagerDashboardChanged: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.useEventsManagerDashboard = action.payload
    },
    useEventsStudentViewChanged: (state, action: PayloadAction<boolean>) => {
      state.useEventsStudentView = action.payload
    },
    useQuizAnalyticsV2Changed: (state, action: PayloadAction<boolean>) => {
      state.useQuizAnalyticsV2 = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.settingsStatus = 'pending'
      })
      .addCase(fetchSettings.rejected, (state) => {
        state.settingsStatus = 'rejected'
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.settingsStatus = 'fulfilled'
        state.settings = action.payload
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.notificationsStatus = 'pending'
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.notificationsStatus = 'rejected'
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notificationsStatus = 'fulfilled'
        state.notifications = action.payload
      })
      .addCase(fetchUser.pending, (state) => {
        state.userStatus = 'pending'
      })
      .addCase(fetchUser.rejected, (state) => {
        state.userStatus = 'rejected'
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.userStatus = 'fulfilled'
        state.user = action.payload
      })
      .addCase(fetchProfileImage.pending, (state) => {
        state.profileImageStatus = 'pending'
      })
      .addCase(fetchProfileImage.rejected, (state) => {
        state.profileImageStatus = 'rejected'
      })
      .addCase(fetchProfileImage.fulfilled, (state, action) => {
        state.profileImageStatus = 'fulfilled'
        state.profileImage = action.payload
      })
      .addCase(fetchStates.pending, (state) => {
        state.statesStatus = 'pending'
      })
      .addCase(fetchStates.rejected, (state) => {
        state.statesStatus = 'rejected'
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.statesStatus = 'fulfilled'
        state.states = action.payload
      })
      .addCase(fetchTimeZones.pending, (state) => {
        state.timeZonesStatus = 'pending'
      })
      .addCase(fetchTimeZones.rejected, (state) => {
        state.timeZonesStatus = 'rejected'
      })
      .addCase(fetchTimeZones.fulfilled, (state, action) => {
        state.timeZonesStatus = 'fulfilled'
        state.timeZones = action.payload
      })
      .addCase(fetchMaintenanceConfig.pending, (state) => {
        state.maintenanceConfigStatus = 'pending'
      })
      .addCase(fetchMaintenanceConfig.rejected, (state) => {
        state.maintenanceConfigStatus = 'rejected'
      })
      .addCase(fetchMaintenanceConfig.fulfilled, (state, action) => {
        state.maintenanceConfigStatus = 'fulfilled'
        state.scheduledMaintenance = action.payload
      })
  },
})

export let {
  sessionStatusChanged,
  featureFlagsFlagEvaluated,
  rolesChnaged,
  emailAddressChanged,
  linkedServicesChanged,
  userIDChanged,
  boundTreeStatusChanged,
  navigationStatusChanged,
  useScribeAccreditationAnalysisChanged,
  useEventsChanged,
  useSubscribePageV2Changed,
  useTranscriptEditorChanged,
  useCreateV4Changed,
  useCreateV5Changed,
  useShowNotesChanged,
  useLiveClassV4Changed,
  useProfileAssignmentsv4Changed,
  useRecertificationCycleChanged,
  useComplianceChanged,
  useAssignmentAnalyticsChanged,
  useGroupsV2Changed,
  useIBSCV2Changed,
  checkoutPriceChanged,
  checkoutPromoCodeChanged,
  checkoutPromoValidationStatusChanged,
  checkoutPromoResponseChanged,
  checkoutClientSecretChanged,
  maintenanceBannerDismissed,
  useMaintenanceModeChanged,
  useTexasJurisprudenceChanged,
  useEventsManagerDashboardChanged,
  useEventsStudentViewChanged,
  useQuizAnalyticsV2Changed,
} = settingsSlice.actions

export let selectSettings = (state: AppRootState) => state.settings.settings
export let selectSettingsStatus = (state: AppRootState) =>
  state.settings.settingsStatus
export let selectSessionStatus = (state: AppRootState) =>
  state.settings.sessionStatus
export let selectFeatureFlagsEvaluated = (state: AppRootState) =>
  state.settings.featureFlagsEvaluted
export let selectRoles = (state: AppRootState) => state.settings.roles
export let selectEmailAddress = (state: AppRootState) =>
  state.settings.emailAddress
export let selectLinkedServices = (state: AppRootState) =>
  state.settings.linkedServices
export let selectUserID = (state: AppRootState) => state.settings.userID
export let selectBoundTreeStatus = (state: AppRootState) =>
  state.settings.boundTreeStatus
export let selectNavigationStatus = (state: AppRootState) =>
  state.settings.navigationStatus

export let selectUseScribeAccreditationAnalysis = (state: AppRootState) =>
  state.settings.useScribeAccreditationAnalysis
export let selectUseEvents = (state: AppRootState) => state.settings.useEvents
export let selectUseSubscribePageV2 = (state: AppRootState) =>
  state.settings.useSubscribePageV2
export let selectUseTranscriptEditor = (state: AppRootState) =>
  state.settings.useTranscriptEditor
export let selectUseCreateV4 = (state: AppRootState) =>
  state.settings.useCreateV4
export let selectUseCreateV5 = (state: AppRootState) =>
  state.settings.useCreateV5
export let selectUseShowNotes = (state: AppRootState) =>
  state.settings.useShowNotes
export let selectUseLiveClassV4 = (state: AppRootState) =>
  state.settings.useLiveClassV4
export let selectUseProfileAssignmentsv4 = (state: AppRootState) =>
  state.settings.useProfileAssignmentsv4
export let selectUseRecertificationCycle = (state: AppRootState) =>
  state.settings.useRecertificationCycle
export let selectUseCompliance = (state: AppRootState) =>
  state.settings.useCompliance
export let selectUseAssignmentAnalytics = (state: AppRootState) =>
  state.settings.useAssignmentAnalytics
export let selectUseGroupsV2 = (state: AppRootState) =>
  state.settings.useGroupsV2
export let selectUseIBSCV2 = (state: AppRootState) => state.settings.useIBSCV2

export let selectNotifications = (state: AppRootState) =>
  state.settings.notifications.reduce(
    (reduced, n) => {
      let message = ''
      let route = ''
      if (n.kind === 'assignmentAssigned') {
        message = `You have a new assignment: ${n.name}`
        route = '/profile/assignments'
      } else if (n.kind === 'assignmentDue') {
        let daysDiff = dayjs(n.dueDate).diff(dayjs(), 'days')
        let dayPrefix = daysDiff < 0 ? 'was' : 'is'
        message = `Your assignment ${n.name} ${dayPrefix} due ${dayDiffText(daysDiff)}.`
        route = '/profile/assignments'
      } else if (n.kind === 'credentialExpiring') {
        let daysDiff = dayjs(n.expirationDate).diff(dayjs(), 'days')
        let dayPrefix = daysDiff < 0 ? 'expired' : 'expires'
        message = `Your ${n.type} credential ${n.credentialNumber} ${dayPrefix} ${dayDiffText(daysDiff)}.`
        route = '/profile/credentials'
      } else if (n.kind === 'compliance') {
        message = `You have been assigned a new compliance requirement: ${n.name}`
        route = '/profile/licenses'
      }
      return [
        ...reduced,
        {
          id: n.id,
          status: n.status,
          message,
          route,
        },
      ]
    },
    [] as (Pick<Notification, 'id' | 'status'> & {
      message: string
      route: string
    })[],
  )
export let selectNoficationsStatus = (state: AppRootState) =>
  state.settings.notificationsStatus

export let selectUser = (state: AppRootState) =>
  state.settings.user
    ? {
        ...state.settings.user,
        name: `${state.settings.user.firstName} ${state.settings.user.lastName}`,
      }
    : undefined
export let selectUserStatus = (state: AppRootState) => state.settings.userStatus

export let selectProfileImage = (state: AppRootState) =>
  state.settings.profileImage
export let selectProfileImageStatus = (state: AppRootState) =>
  state.settings.profileImageStatus

export let selectCheckoutPrice = (state: AppRootState) =>
  state.settings.checkoutPrice
export let selectCheckoutPromoCode = (state: AppRootState) =>
  state.settings.checkoutPromoCode
export let selectCheckoutPromoValidationStatus = (state: AppRootState) =>
  state.settings.checkoutPromoValidationStatus
export let selectCheckoutPromoResponse = (state: AppRootState) =>
  state.settings.checkoutPromoResponse
export let selectCheckoutClientSecret = (state: AppRootState) =>
  state.settings.checkoutClientSecret

export let selectStates = (state: AppRootState) => state.settings.states
export let selectStatesStatus = (state: AppRootState) =>
  state.settings.statesStatus

export let selectTimeZones = (state: AppRootState) => state.settings.timeZones
export let selectTimeZonesStatus = (state: AppRootState) =>
  state.settings.timeZonesStatus

export let selectScheduledMaintenance = (state: AppRootState) =>
  state.settings.scheduledMaintenance
export let selectMaintenanceConfigStatus = (state: AppRootState) =>
  state.settings.maintenanceConfigStatus
export let selectMaintenanceBannerDismissed = (state: AppRootState) =>
  state.settings.maintenanceBannerDismissed
export let selectUseMaintenanceMode = (state: AppRootState) =>
  state.settings.useMaintenanceMode
export let selectUseTexasJurisprudence = (state: AppRootState) =>
  state.settings.useTexasJurisprudence
export let selectUseEventsManagerDashboard = (state: AppRootState) =>
  state.settings.useEventsManagerDashboard
export let selectUseEventsStudentView = (state: AppRootState) =>
  state.settings.useEventsStudentView
export let selectUseQuizAnalyticsV2 = (state: AppRootState) =>
  state.settings.useQuizAnalyticsV2

export let TEXAS_JURISPRUDENCE_COURSE_IDS = [
  '098b08e8-21cd-4f13-8900-c8778bc67be7',
]

export default settingsSlice.reducer
